import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import imageCover from '../../../../../assets/img/imageCover.png';
import { TFile } from '../../../user/caseDraft/stages/cushing-v2/secondStage/SecondStage.types';

const FRAME_SIZE = 163;

interface Props {
  file: TFile | null | undefined;
  label?: string;
}

export const AttachmentThumbnail = ({ file, label = 'Załącznik' }: Props) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [coverSize, setCoverSize] = useState<{ w: number; h: number } | null>(null);

  const src = file?.link ? (file.type === 'pdf' ? imageCover : file.link) : '';

  useEffect(() => {
    if (!src) return;
    const img = imgRef.current;
    if (!img) return;

    // html2canvas ignores `object-fit: cover` on <img> and stretches the bitmap
    // to the container's width/height (behaves like `fill`). We work around it
    // by measuring the natural aspect ratio and setting explicit width/height
    // that preserve it — the parent `overflow: hidden` + absolute centering
    // produces the same visual cover without relying on object-fit.
    const measure = () => {
      const { naturalWidth: w, naturalHeight: h } = img;
      if (!w || !h) return;
      const aspect = w / h;
      if (aspect >= 1) {
        setCoverSize({ w: FRAME_SIZE * aspect, h: FRAME_SIZE });
      } else {
        setCoverSize({ w: FRAME_SIZE, h: FRAME_SIZE / aspect });
      }
    };

    if (img.complete && img.naturalWidth > 0) {
      measure();
      return;
    }
    img.addEventListener('load', measure);
    return () => img.removeEventListener('load', measure);
  }, [src]);

  if (!file?.link) {
    return null;
  }

  return (
    <Box
      mt={2}
      display={'flex'}
      flexDirection={'column'}
      alignItems={'flex-start'}
      data-pdf-keep-together
    >
      <Typography variant={'body1'}>{label}</Typography>
      <Box
        sx={{
          width: FRAME_SIZE,
          height: FRAME_SIZE,
          overflow: 'hidden',
          borderRadius: 1,
          cursor: 'pointer',
          mt: 0.5,
          position: 'relative',
        }}
        onClick={() => window.open(file.link, '_blank')}
      >
        <img
          ref={imgRef}
          src={src}
          alt={file.name}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'block',
            width: coverSize ? coverSize.w : '100%',
            height: coverSize ? coverSize.h : '100%',
            objectFit: 'cover',
          }}
        />
      </Box>
      <Typography mt={1} variant={'body1'}>
        {file.name}
      </Typography>
    </Box>
  );
};
